import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { denormalize } from 'normalizr';
import { noop } from 'lodash';
import { SCHEMAS } from 'js/api/schemas';
import {
  createOrgSnapshot,
  listAssessmentHistorySummaries,
  listOrgSnapshotsForSource,
  load as loadOrg,
  lookupOrgRubricScores,
  lookupQuestionScores,
} from 'js/actions/org-actions';
import { listSubscriptions } from 'js/actions/assessment-template-actions';
import {
  selectAssessmentHistorySummaries,
  selectOrgSnapshotSetQuery,
  unwrapQuestionScoresResult,
} from 'js/store/reducers/queries';
import { useQuery } from 'js/hooks/useQuery';
import { unwrapApiResult } from 'js/utils/api';
import assessmentTemplates from 'js/data/assessment-templates';
import {
  rubricDocumentToOrgTemplate,
  rubricTeamsToQuestionScores,
} from 'js/utils/rubric-org-scores';
import OrgSnapshotCreate from '../components/OrgSnapshots/OrgSnapshotCreate';
import {
  joinOrgSnapshotCreateChannel,
  liveAsOfHasScores,
  optionalSnapshotName,
  orgSnapshotTopic,
  randomNonce,
  snapshotIdFromCreatePayload,
  snapshotReportPath,
  snapshotSetPath,
  snapshotSourceName,
} from '../components/OrgSnapshots/org-snapshot-utils';

function transformTemplate(template) {
  return {
    ...template,
    key: template.key ?? template.assessment.key,
    assessment: {
      ...template.assessment,
      document: template.assessment,
      question_sets: template.assessment.pages
        .filter((p) => p.pageType === 'question-set')
        .map((qs) => ({
          ...qs,
          questions: qs.elements
            .filter((e) => e.elementType === 'question')
            .map((q) => ({
              ...q,
              answers: q.answers?.map((a) => {
                const newAnswer = { ...a, guid: a.id };
                delete newAnswer.id;
                return newAnswer;
              }),
            })),
        })),
    },
  };
}

function asRows(result) {
  return Array.isArray(result) ? result : [];
}

function OrgSnapshotCreateContainer({
  organization,
  workspace,
  assessmentHistorySummaries,
  snapshotsQuery,
  token,
  loadOrg,
  listAssessmentHistorySummaries,
  listOrgSnapshotsForSource,
  listSubscriptions,
  lookupQuestionScores,
  lookupOrgRubricScores,
  createOrgSnapshot,
}) {
  const { workspaceId, orgId, sourceType, sourceKey } = useParams();
  const history = useHistory();
  const decodedKey = useMemo(() => decodeURIComponent(sourceKey), [sourceKey]);
  const setPath = snapshotSetPath(workspaceId, orgId, sourceType, decodedKey);

  const createChannelRef = useRef(null);
  const createCancelledRef = useRef(false);

  const [previewEndDate, setPreviewEndDate] = useState(null);
  const [liveData, setLiveData] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [progress, setProgress] = useState(null);
  const [createError, setCreateError] = useState(null);

  const refreshSubscriptionsQuery = useCallback(
    (qid) => listSubscriptions(qid),
    [listSubscriptions]
  );
  const {
    refresh: refreshSubscriptions,
    result: subscriptions,
    hasLoaded: subscriptionsHaveLoaded,
  } = useQuery({
    queryId: `subscriptions-${workspaceId}`,
    query: refreshSubscriptionsQuery,
    schema: SCHEMAS.ASSESSMENT_DOC_TEMPLATE_SUBSCRIPTION_ARRAY,
  });

  useEffect(() => {
    loadOrg(orgId).catch(noop);
    listAssessmentHistorySummaries(orgId).catch(noop);
    listOrgSnapshotsForSource(orgId, sourceType, decodedKey, {}).catch(noop);
    refreshSubscriptions();
  }, [
    decodedKey,
    listAssessmentHistorySummaries,
    listOrgSnapshotsForSource,
    loadOrg,
    orgId,
    refreshSubscriptions,
    sourceType,
  ]);

  useEffect(() => {
    createCancelledRef.current = false;
    return () => {
      createCancelledRef.current = true;
      createChannelRef.current?.leave();
      createChannelRef.current = null;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!previewEndDate) {
      return undefined;
    }
    setLiveLoading(true);
    const req =
      sourceType === 'rubric'
        ? lookupOrgRubricScores(orgId, decodedKey, { end_date: previewEndDate })
        : lookupQuestionScores(orgId, decodedKey, { end_date: previewEndDate });
    req
      .then((data) => {
        if (!cancelled) {
          setLiveData(unwrapApiResult(data));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLiveData(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLiveLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    decodedKey,
    lookupOrgRubricScores,
    lookupQuestionScores,
    orgId,
    previewEndDate,
    sourceType,
  ]);

  const handleAsOfChange = useCallback((endDateIso) => {
    setPreviewEndDate(endDateIso);
  }, []);

  const handleCreateSnapshot = useCallback(
    async ({ name, snapshotAt }) => {
      if (creating || !liveAsOfHasScores(sourceType, liveData) || !snapshotAt) {
        return;
      }
      if (new Date(snapshotAt).getTime() > Date.now() + 1000) {
        return;
      }

      const nonce = randomNonce();
      const topic = orgSnapshotTopic(nonce);
      setCreating(true);
      setCreateError(null);
      setProgress({ stage: 'waiting', teams_done: 0, teams_total: 0 });

      const handlers = {
        onProgress: (payload) => setProgress(payload),
        onComplete: (payload) => {
          if (createCancelledRef.current) {
            return;
          }
          createChannelRef.current = null;
          setCreating(false);
          setProgress(null);
          const snapshotId = snapshotIdFromCreatePayload(payload);
          history.push(
            snapshotId
              ? snapshotReportPath(
                  workspaceId,
                  orgId,
                  sourceType,
                  decodedKey,
                  snapshotId
                )
              : setPath
          );
        },
        onError: (payload) => {
          if (createCancelledRef.current) {
            return;
          }
          createChannelRef.current = null;
          setCreating(false);
          setProgress(null);
          setCreateError(payload?.reason || 'Snapshot create failed');
        },
      };

      try {
        await createOrgSnapshot(orgId, {
          name: optionalSnapshotName(name),
          source_type: sourceType,
          source_key: decodedKey,
          snapshot_at: snapshotAt,
          nonce,
        });
        if (createCancelledRef.current) {
          return;
        }
        const joined = await joinOrgSnapshotCreateChannel(
          token,
          topic,
          handlers,
          { workspace_id: workspaceId }
        );
        if (createCancelledRef.current) {
          joined?.leave();
          return;
        }
        createChannelRef.current = joined;
      } catch (err) {
        createChannelRef.current = null;
        if (createCancelledRef.current) {
          return;
        }
        setCreating(false);
        setProgress(null);
        setCreateError(err?.reason || err?.message || 'Snapshot create failed');
      }
    },
    [
      createOrgSnapshot,
      creating,
      decodedKey,
      history,
      liveData,
      orgId,
      setPath,
      sourceType,
      token,
      workspaceId,
    ]
  );

  const snapshotRows = asRows(snapshotsQuery.data);
  const templates = useMemo(() => {
    if (!subscriptionsHaveLoaded) {
      return null;
    }
    return assessmentTemplates.concat(
      subscriptions?.map(transformTemplate) ?? []
    );
  }, [subscriptions, subscriptionsHaveLoaded]);

  const sourceName = snapshotSourceName({
    sourceKey: decodedKey,
    snapshots: snapshotRows,
    summaries: assessmentHistorySummaries,
    fallbackName: liveData?.rubric_name,
  });

  const template = useMemo(() => {
    if (sourceType === 'rubric' && liveData?.document) {
      return rubricDocumentToOrgTemplate(
        liveData.rubric_id || decodedKey,
        liveData.rubric_name || sourceName,
        liveData.document,
        previewEndDate ? new Date(previewEndDate) : new Date()
      );
    }
    const liveTemplate = templates?.find(({ key }) => key === decodedKey);
    if (liveTemplate) {
      return liveTemplate;
    }
    return {
      key: decodedKey,
      name: sourceName,
      type: sourceType === 'rubric' ? 'rubric' : 'survey',
      assessment: { question_sets: [] },
    };
  }, [decodedKey, liveData, previewEndDate, sourceName, sourceType, templates]);

  const { teams: liveQuestionScores, catalog: liveCatalog } = useMemo(() => {
    if (!liveData) {
      return { teams: null, catalog: null };
    }
    if (sourceType === 'rubric') {
      return {
        teams: liveData.teams
          ? rubricTeamsToQuestionScores(
              liveData.teams,
              liveData.document,
              previewEndDate ? new Date(previewEndDate) : new Date()
            )
          : null,
        catalog: null,
      };
    }
    return unwrapQuestionScoresResult(liveData);
  }, [liveData, previewEndDate, sourceType]);

  return (
    <OrgSnapshotCreate
      organization={organization}
      workspace={workspace}
      sourceName={sourceName}
      template={template}
      liveQuestionScores={liveQuestionScores}
      liveCatalog={liveCatalog}
      liveLoading={liveLoading}
      hasScorableScores={liveAsOfHasScores(sourceType, liveData)}
      creating={creating}
      progress={progress}
      createError={createError}
      onAsOfChange={handleAsOfChange}
      onCreateSnapshot={handleCreateSnapshot}
    />
  );
}

export default connect(
  (store, { match }) => {
    const orgId = match?.params?.orgId;
    const workspaceId = match?.params?.workspaceId;
    const sourceType = match?.params?.sourceType;
    const sourceKey = match?.params?.sourceKey
      ? decodeURIComponent(match.params.sourceKey)
      : match?.params?.sourceKey;
    return {
      organization: denormalize(
        store.entities.organizations[orgId],
        SCHEMAS.ORGANIZATION,
        store.entities
      ),
      workspace: store.entities.workspaces[workspaceId],
      assessmentHistorySummaries:
        selectAssessmentHistorySummaries(store, orgId)?.data ?? [],
      snapshotsQuery: selectOrgSnapshotSetQuery(
        store,
        orgId,
        sourceType,
        sourceKey,
        false
      ),
      token: store.identity.token,
    };
  },
  {
    loadOrg,
    listAssessmentHistorySummaries,
    listOrgSnapshotsForSource,
    listSubscriptions,
    lookupQuestionScores,
    lookupOrgRubricScores,
    createOrgSnapshot,
  }
)(OrgSnapshotCreateContainer);
